/**
 * Lambda Data Service
 * Provides direct Lambda invocation for RDS data access
 * Temporary solution until API Gateway is properly configured
 */
import { logger } from '@/utils/logger';

// For now, we'll use a mock service that returns the same data structure as Lambda
// This allows the frontend to work while we set up proper API Gateway integration
class LambdaDataService {
  private static instance: LambdaDataService;
  private baseURL: string;

  private constructor() {
    // Real API Gateway URL for iWishBag Data API
    this.baseURL = 'https://1pj4haeky3.execute-api.ap-south-1.amazonaws.com/prod';
    logger.info('[LambdaDataService] Initialized with API Gateway URL:', this.baseURL);
  }

  static getInstance(): LambdaDataService {
    if (!LambdaDataService.instance) {
      LambdaDataService.instance = new LambdaDataService();
    }
    return LambdaDataService.instance;
  }

  /**
   * Get all countries from RDS via Lambda
   */
  async getCountries(): Promise<{
    success: boolean;
    data: Array<{
      code: string;
      name: string;
      currency: string;
      rate_from_usd: number;
      additional_shipping: number;
      sales_tax: number;
      vat: number;
      min_shipping: number;
      weight_unit?: string;
      additional_weight?: number;
      shipping_allowed?: boolean;
      purchase_allowed?: boolean;
    }>;
    count: number;
  }> {
    try {
      const response = await fetch(`${this.baseURL}/countries`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      logger.error('[LambdaDataService] Error fetching countries:', error);
      throw error;
    }
  }

  /**
   * Get all currencies from RDS via Lambda
   */
  async getCurrencies(): Promise<{
    success: boolean;
    data: Array<{
      code: string;
      name: string;
      symbol: string;
      rate_from_usd: number;
      decimal_places: number;
      is_active: boolean;
      thousand_separator: string;
      decimal_separator: string;
    }>;
    count: number;
  }> {
    try {
      const response = await fetch(`${this.baseURL}/currencies`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      logger.error('[LambdaDataService] Error fetching currencies:', error);
      throw error;
    }
  }

  /**
   * Get countries filtered by shipping allowed flag
   */
  async getShippingCountries(): Promise<{
    success: boolean;
    data: Array<{
      code: string;
      name: string;
      currency: string;
    }>;
    count: number;
  }> {
    try {
      const countries = await this.getCountries();
      const shippingCountries = countries.data
        .filter(country => country.shipping_allowed === true)
        .map(country => ({
          code: country.code,
          name: country.name,
          currency: country.currency
        }));

      return {
        success: true,
        data: shippingCountries,
        count: shippingCountries.length
      };
    } catch (error) {
      logger.error('[LambdaDataService] Error fetching shipping countries:', error);
      throw error;
    }
  }

  /**
   * Get countries filtered by purchase allowed flag
   */
  async getPurchaseCountries(): Promise<{
    success: boolean;
    data: Array<{
      code: string;
      name: string;
      currency: string;
    }>;
    count: number;
  }> {
    try {
      const countries = await this.getCountries();
      const purchaseCountries = countries.data
        .filter(country => country.purchase_allowed === true)
        .map(country => ({
          code: country.code,
          name: country.name,
          currency: country.currency
        }));

      return {
        success: true,
        data: purchaseCountries,
        count: purchaseCountries.length
      };
    } catch (error) {
      logger.error('[LambdaDataService] Error fetching purchase countries:', error);
      throw error;
    }
  }

  /**
   * Get specific country settings
   */
  async getCountrySettings(countryCode: string): Promise<{
    code: string;
    name: string;
    currency: string;
    rate_from_usd: number;
    additional_shipping: number;
    sales_tax: number;
    vat: number;
    min_shipping: number;
    weight_unit?: string;
    additional_weight?: number;
    shipping_allowed?: boolean;
    purchase_allowed?: boolean;
  } | null> {
    try {
      const countries = await this.getCountries();
      return countries.data.find(country => country.code === countryCode) || null;
    } catch (error) {
      logger.error(`[LambdaDataService] Error fetching country settings for ${countryCode}:`, error);
      return null;
    }
  }

  /**
   * Get system settings from RDS via Lambda
   */
  async getSystemSettings(settingKeys?: string[]): Promise<{
    success: boolean;
    data: Array<{
      setting_key: string;
      setting_value: string;
      description: string;
      is_active: boolean;
      updated_at: string;
    }>;
    count: number;
  }> {
    try {
      let url = `${this.baseURL}/system-settings`;
      if (settingKeys && settingKeys.length > 0) {
        url += `?setting_key=${settingKeys.join(',')}`;
      }

      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      logger.error('[LambdaDataService] Error fetching system settings:', error);
      throw error;
    }
  }

  /**
   * Get shipping tiers from RDS via Lambda
   */
  async getShippingTiers(filters?: { scope?: string; tier_type?: string }): Promise<{
    success: boolean;
    data: Array<{
      id: number;
      scope: string;
      tier_type: string;
      direct_exchange_rate: number | null;
      updated_at: string;
      active: boolean;
    }>;
    count: number;
  }> {
    try {
      let url = `${this.baseURL}/shipping-tiers`;
      const params = new URLSearchParams();

      if (filters?.scope) {
        params.append('scope', filters.scope);
      }
      if (filters?.tier_type) {
        params.append('tier_type', filters.tier_type);
      }

      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      logger.error('[LambdaDataService] Error fetching shipping tiers:', error);
      throw error;
    }
  }

  // =====================================================
  // CATALOG ENDPOINTS
  // =====================================================

  /**
   * Get all collections from RDS via Lambda
   */
  async getCollections(filters?: {
    featured?: boolean;
    status?: string;
  }): Promise<{
    success: boolean;
    data: Array<{
      id: string;
      name: string;
      handle: string;
      description?: string;
      featured_image?: string;
      featured_image_alt?: string;
      seo_title?: string;
      seo_description?: string;
      sort_order: number;
      is_featured: boolean;
      parent_collection_id?: string;
      status: string;
      created_at: string;
      updated_at: string;
    }>;
    count: number;
  }> {
    try {
      let url = `${this.baseURL}/collections`;
      const params = new URLSearchParams();

      if (filters?.featured !== undefined) {
        params.append('featured', filters.featured.toString());
      }
      if (filters?.status) {
        params.append('status', filters.status);
      }

      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      logger.error('[LambdaDataService] Error fetching collections:', error);
      throw error;
    }
  }

  /**
   * Get collection by handle from RDS via Lambda
   */
  async getCollectionByHandle(handle: string): Promise<{
    id: string;
    name: string;
    handle: string;
    description?: string;
    featured_image?: string;
    featured_image_alt?: string;
    seo_title?: string;
    seo_description?: string;
    sort_order: number;
    is_featured: boolean;
    parent_collection_id?: string;
    status: string;
    created_at: string;
    updated_at: string;
  } | null> {
    try {
      const response = await fetch(`${this.baseURL}/collections?handle=${encodeURIComponent(handle)}`);

      if (!response.ok) {
        if (response.status === 404) {
          return null;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.success ? data.data : null;
    } catch (error) {
      logger.error(`[LambdaDataService] Error fetching collection by handle ${handle}:`, error);
      return null;
    }
  }

  /**
   * Get products from RDS via Lambda
   */
  async getProducts(filters?: {
    handle?: string;
    collection_handle?: string;
    brand?: string;
    status?: string;
    sort_by?: string;
    limit?: number;
    offset?: number;
    search?: string;
  }): Promise<{
    success: boolean;
    data: Array<{
      id: string;
      title: string;
      handle: string;
      brand?: string;
      description?: string;
      collection_id?: string;
      collection_handle?: string;
      price_origin: number;
      compare_at_price_origin?: number;
      origin_currency: string;
      featured_image?: string;
      images: string[];
      has_variants: boolean;
      availability: string;
      weight_kg?: number;
      requires_shipping: boolean;
      taxable: boolean;
      seo_title?: string;
      seo_description?: string;
      search_keywords: string[];
      category_tags: string[];
      hsn_code?: string;
      product_type?: string;
      view_count: number;
      quote_request_count: number;
      popularity_score: number;
      status: string;
      published_at?: string;
      created_at: string;
      updated_at: string;
    }>;
    count: number;
  }> {
    try {
      let url = `${this.baseURL}/products`;
      const params = new URLSearchParams();

      if (filters?.handle) {
        params.append('handle', filters.handle);
      }
      if (filters?.collection_handle) {
        params.append('collection_handle', filters.collection_handle);
      }
      if (filters?.brand) {
        params.append('brand', filters.brand);
      }
      if (filters?.status) {
        params.append('status', filters.status);
      }
      if (filters?.sort_by) {
        params.append('sort_by', filters.sort_by);
      }
      if (filters?.limit) {
        params.append('limit', filters.limit.toString());
      }
      if (filters?.offset) {
        params.append('offset', filters.offset.toString());
      }
      if (filters?.search) {
        params.append('search', filters.search);
      }

      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      logger.error('[LambdaDataService] Error fetching products:', error);
      throw error;
    }
  }

  /**
   * Get product by handle from RDS via Lambda
   */
  async getProductByHandle(handle: string): Promise<{
    id: string;
    title: string;
    handle: string;
    brand?: string;
    description?: string;
    collection_id?: string;
    collection_handle?: string;
    price_origin: number;
    compare_at_price_origin?: number;
    origin_currency: string;
    featured_image?: string;
    images: string[];
    has_variants: boolean;
    availability: string;
    weight_kg?: number;
    requires_shipping: boolean;
    taxable: boolean;
    seo_title?: string;
    seo_description?: string;
    search_keywords: string[];
    category_tags: string[];
    hsn_code?: string;
    product_type?: string;
    view_count: number;
    quote_request_count: number;
    popularity_score: number;
    status: string;
    published_at?: string;
    created_at: string;
    updated_at: string;
  } | null> {
    try {
      const response = await fetch(`${this.baseURL}/products?handle=${encodeURIComponent(handle)}`);

      if (!response.ok) {
        if (response.status === 404) {
          return null;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.success ? data.data : null;
    } catch (error) {
      logger.error(`[LambdaDataService] Error fetching product by handle ${handle}:`, error);
      return null;
    }
  }

  /**
   * Get products by collection from RDS via Lambda
   */
  async getProductsByCollection(
    collectionHandle: string,
    options?: {
      sort_by?: string;
      limit?: number;
      offset?: number;
    }
  ): Promise<{
    success: boolean;
    data: Array<any>;
    count: number;
  }> {
    try {
      const filters = {
        collection_handle: collectionHandle,
        ...options
      };

      return await this.getProducts(filters);
    } catch (error) {
      logger.error(`[LambdaDataService] Error fetching products for collection ${collectionHandle}:`, error);
      throw error;
    }
  }
}

// Export singleton instance
export const lambdaDataService = LambdaDataService.getInstance();